/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Draft, PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { SoundingsCollection } from '@opengeoweb/shared';
import type { SetTimeSyncActionPayload } from '@opengeoweb/store';
import type {
  SoundingsState,
  SoundingsViewState,
  RegisterSoundingsPresetPayload,
  UpdateLocationPayload,
  UpdateSourcePayload,
  UpdateTimePayload,
  UpdateDiagramTypePayload,
  ToggleLocationMarkersPayload,
  ToggleTimeSliderVisibilityPayload,
  ToggleLegendVisibilityPayload,
  UpdateProfileDataPreferencesPayload,
  AddSoundingPanelsPayload,
  ChangeSoundingPanelOrderPayload,
  SetLocationMenuSizeAndPositionPayload,
  SetLocationsMenuOpenPayload,
} from './types';
import { getViewIdFromAction, isRegisteredViewId } from './utils';
import { DEFAULT_PROFILE_DATA_PREFERENCES } from '../utils/profileDataPreferences';

/**
 * Default state every new soundings view starts with. Views are created
 * dynamically when a soundings view mounts, so this serves as the template
 * for each new view added to the store.
 */
export const initialSoundingsViewState: SoundingsViewState = {
  selectedSources: {},
  selectedLocations: {},
  selectedTime: new Date().toISOString(),
  diagramType: 'SKEWT',
  soundingPanels: {
    order: ['soundings', 'windbarbs', 'hodograph', 'profiledatatable'],
    entities: {
      soundings: {
        id: 'soundings',
        name: 'Soundings',
        isDisplayed: true,
        isOpen: true,
      },
      windbarbs: {
        id: 'windbarbs',
        name: 'Wind Barbs',
        isDisplayed: true,
        isOpen: false,
      },
      hodograph: {
        id: 'hodograph',
        name: 'Hodograph',
        isDisplayed: true,
        isOpen: false,
      },
      profiledatatable: {
        id: 'profiledatatable',
        name: 'Profile Data',
        isDisplayed: true,
      },
    },
  },
  shouldHideLocationMarkers: false,
  isTimeSliderVisible: true,
  isLegendVisible: true,
  profileDataPreferences: DEFAULT_PROFILE_DATA_PREFERENCES,
  syncGroupsIds: [],
};

/**
 * Creates a new independent copy of the default view state.
 * Each view needs its own copy so that changes to one view
 * don't accidentally affect another.
 */
const createSoundingsViewState = (): SoundingsViewState => ({
  ...initialSoundingsViewState,
  selectedSources: {},
  selectedLocations: {},
  soundingPanels: {
    order: [...initialSoundingsViewState.soundingPanels.order],
    entities: Object.fromEntries(
      Object.entries(initialSoundingsViewState.soundingPanels.entities).map(
        ([key, value]) => [key, { ...value }],
      ),
    ),
  },
});

const initialState: SoundingsState = {
  byId: {},
  registeredSoundingsViewIds: [],
  soundingsCollections: [],
  resizeOnViewCloseSignal: 0,
};

/**
 * Looks up the view targeted by an action's `viewId`. Since views are
 * added dynamically, the targeted view may not exist yet — in that case
 * it gets initialized with default state. Returns `undefined` for
 * unregistered views so the reducer safely skips the action.
 */
const resolveSoundingsView = (
  draft: Draft<SoundingsState>,
  action: { payload: { viewId?: string } },
): SoundingsViewState | undefined => {
  const viewId = getViewIdFromAction(action);
  if (!viewId || !isRegisteredViewId(draft, viewId)) {
    return undefined;
  }
  if (!draft.byId[viewId]) {
    draft.byId[viewId] = createSoundingsViewState();
  }
  return draft.byId[viewId];
};

const slice = createSlice({
  initialState,
  name: 'soundings',
  reducers: {
    registerSoundingsView: (
      draft: Draft<SoundingsState>,
      payloadAction: PayloadAction<{ viewId: string }>,
    ) => {
      const { viewId } = payloadAction.payload;
      if (!viewId) {
        return;
      }
      if (!draft.registeredSoundingsViewIds?.includes(viewId)) {
        draft.registeredSoundingsViewIds = [
          ...(draft.registeredSoundingsViewIds ?? []),
          viewId,
        ];
      }
      if (!draft.byId[viewId]) {
        draft.byId[viewId] = createSoundingsViewState();
      }
    },
    registerSoundingsPreset: (
      draft: Draft<SoundingsState>,
      payloadAction: PayloadAction<RegisterSoundingsPresetPayload>,
    ) => {
      const panel = resolveSoundingsView(draft, payloadAction);
      if (!panel) {
        return;
      }
      const { preset } = payloadAction.payload;
      if (preset) {
        panel.selectedSources = preset.selectedSources ?? {};
        panel.selectedLocations = preset.selectedLocations ?? {};
        panel.selectedTime = preset.selectedTime ?? new Date().toISOString();
        panel.diagramType = preset.diagramType ?? 'SKEWT';
        // Ensure we show all available panels, even if not stored in preset
        if (preset.soundingPanels) {
          panel.soundingPanels = { ...preset.soundingPanels };
          initialSoundingsViewState.soundingPanels.order.forEach((panelKey) => {
            if (
              !preset.soundingPanels.order.find(
                (presetPanel) => panelKey === presetPanel,
              )
            ) {
              panel.soundingPanels.order = [
                ...panel.soundingPanels.order,
                panelKey,
              ];
              panel.soundingPanels.entities = {
                ...panel.soundingPanels.entities,
                [panelKey]:
                  initialSoundingsViewState.soundingPanels.entities[panelKey],
              };
            }
          });
        } else {
          panel.soundingPanels = initialSoundingsViewState.soundingPanels;
        }
        panel.shouldHideLocationMarkers =
          preset.shouldHideLocationMarkers ?? false;
        panel.isTimeSliderVisible = preset.isTimeSliderVisible ?? true;
        panel.isLegendVisible = preset.isLegendVisible ?? true;
        panel.profileDataPreferences =
          preset.profileDataPreferences ?? DEFAULT_PROFILE_DATA_PREFERENCES;
        panel.locationMenuSize = preset.locationMenuSize;
        panel.locationMenuPosition = preset.locationMenuPosition;
        panel.isLocationsDialogOpen = preset.isLocationsDialogOpen;
      }
    },
    setSoundingsCollections: (
      draft: Draft<SoundingsState>,
      payloadAction: PayloadAction<SoundingsCollection[]>,
    ) => {
      if (payloadAction.payload === draft.soundingsCollections) {
        return;
      }
      draft.soundingsCollections = payloadAction.payload;
    },
    updateSelectedSource: (
      draft: Draft<SoundingsState>,
      payloadAction: PayloadAction<UpdateSourcePayload>,
    ) => {
      const panel = resolveSoundingsView(draft, payloadAction);
      if (!panel) {
        return;
      }
      const { sourceId, value } = payloadAction.payload;
      panel.selectedSources[sourceId] = {
        ...panel.selectedSources[sourceId],
        ...value,
      };
    },
    updateSelectedLocation: (
      draft: Draft<SoundingsState>,
      payloadAction: PayloadAction<UpdateLocationPayload>,
    ) => {
      const panel = resolveSoundingsView(draft, payloadAction);
      if (!panel) {
        return;
      }
      const { sourceId, location } = payloadAction.payload;
      panel.selectedLocations[sourceId] = location;
    },
    updateSelectedTime: (
      draft: Draft<SoundingsState>,
      payloadAction: PayloadAction<UpdateTimePayload>,
    ) => {
      const panel = resolveSoundingsView(draft, payloadAction);
      if (!panel) {
        return;
      }
      const { selectedTime } = payloadAction.payload;
      panel.selectedTime = selectedTime;
    },
    updateDiagramType: (
      draft: Draft<SoundingsState>,
      payloadAction: PayloadAction<UpdateDiagramTypePayload>,
    ) => {
      const panel = resolveSoundingsView(draft, payloadAction);
      if (!panel) {
        return;
      }
      panel.diagramType = payloadAction.payload.diagramType;
    },
    toggleLocationMarkers: (
      draft: Draft<SoundingsState>,
      payloadAction: PayloadAction<ToggleLocationMarkersPayload>,
    ) => {
      const panel = resolveSoundingsView(draft, payloadAction);
      if (!panel) {
        return;
      }
      panel.shouldHideLocationMarkers =
        payloadAction.payload.shouldHideLocationMarkers;
    },
    toggleTimeSliderVisibility: (
      draft: Draft<SoundingsState>,
      payloadAction: PayloadAction<ToggleTimeSliderVisibilityPayload>,
    ) => {
      const panel = resolveSoundingsView(draft, payloadAction);
      if (!panel) {
        return;
      }
      panel.isTimeSliderVisible = payloadAction.payload.isTimeSliderVisible;
    },
    toggleLegendVisibility: (
      draft: Draft<SoundingsState>,
      payloadAction: PayloadAction<ToggleLegendVisibilityPayload>,
    ) => {
      const panel = resolveSoundingsView(draft, payloadAction);
      if (!panel) {
        return;
      }
      panel.isLegendVisible = payloadAction.payload.isLegendVisible;
    },
    updateProfileDataPreferences: (
      draft: Draft<SoundingsState>,
      payloadAction: PayloadAction<UpdateProfileDataPreferencesPayload>,
    ) => {
      const panel = resolveSoundingsView(draft, payloadAction);
      if (!panel) {
        return;
      }
      panel.profileDataPreferences =
        payloadAction.payload.profileDataPreferences;
    },
    addSoundingPanelsFromPreset: (
      draft: Draft<SoundingsState>,
      payloadAction: PayloadAction<AddSoundingPanelsPayload>,
    ) => {
      const panel = resolveSoundingsView(draft, payloadAction);
      if (!panel) {
        return;
      }
      const { order, entities } = payloadAction.payload.panels;
      panel.soundingPanels.order = order;
      panel.soundingPanels.entities = entities;
    },
    changeSoundingPanelOrder: (
      draft: Draft<SoundingsState>,
      payloadAction: PayloadAction<ChangeSoundingPanelOrderPayload>,
    ) => {
      const panel = resolveSoundingsView(draft, payloadAction);
      if (!panel) {
        return;
      }
      panel.soundingPanels.order = payloadAction.payload.newOrderOfIds;
    },
    toggleSoundingPanelVisibility: (
      draft: Draft<SoundingsState>,
      payloadAction: PayloadAction<{
        id: string;
        viewId?: string;
        origin?: string;
      }>,
    ) => {
      const panel = resolveSoundingsView(draft, payloadAction);
      if (!panel) {
        return;
      }
      const { id } = payloadAction.payload;
      const soundingPanel = panel.soundingPanels.entities[id];
      if (soundingPanel) {
        soundingPanel.isDisplayed = !soundingPanel.isDisplayed;
      }
    },
    setPanelIsOpen: (
      draft: Draft<SoundingsState>,
      payloadAction: PayloadAction<{
        panelId: string;
        viewId?: string;
        isOpen: boolean;
        origin?: string;
      }>,
    ) => {
      const panel = resolveSoundingsView(draft, payloadAction);
      if (!panel) {
        return;
      }
      const { panelId, isOpen } = payloadAction.payload;
      if (!panel.soundingPanels.entities[panelId]) {
        return;
      }
      panel.soundingPanels.entities[panelId] = {
        ...panel.soundingPanels.entities[panelId],
        isOpen,
      };
    },
    setPanelCustomWidth: (
      draft: Draft<SoundingsState>,
      payloadAction: PayloadAction<{
        panelId: string;
        viewId?: string;
        customWidth: number;
        origin?: string;
      }>,
    ) => {
      const panel = resolveSoundingsView(draft, payloadAction);
      if (!panel) {
        return;
      }
      const { panelId, customWidth } = payloadAction.payload;
      if (!panel.soundingPanels.entities[panelId]) {
        return;
      }
      panel.soundingPanels.entities[panelId] = {
        ...panel.soundingPanels.entities[panelId],
        customWidth,
      };
    },
    setLocationMenuSizeAndPosition: (
      draft: Draft<SoundingsState>,
      payloadAction: PayloadAction<SetLocationMenuSizeAndPositionPayload>,
    ) => {
      const panel = resolveSoundingsView(draft, payloadAction);
      if (!panel) {
        return;
      }
      panel.locationMenuSize = payloadAction.payload.size;
      if (payloadAction.payload.position !== undefined) {
        panel.locationMenuPosition = payloadAction.payload.position;
      }
    },
    setLocationsMenuOpen: (
      draft: Draft<SoundingsState>,
      payloadAction: PayloadAction<SetLocationsMenuOpenPayload>,
    ) => {
      const panel = resolveSoundingsView(draft, payloadAction);
      if (!panel) {
        return;
      }
      panel.isLocationsDialogOpen = payloadAction.payload.isLocationsDialogOpen;
    },
    notifyViewClosed: (draft: Draft<SoundingsState>) => {
      draft.resizeOnViewCloseSignal = (draft.resizeOnViewCloseSignal ?? 0) + 1;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      'GENERIC_SYNC_SETTIME',
      (draft: Draft<SoundingsState>, action) => {
        const { source, targets: targetsFromAction } = (
          action as PayloadAction<SetTimeSyncActionPayload>
        ).payload;
        const targets = [...targetsFromAction];

        if (source?.payload) {
          targets.unshift({
            targetId: source.payload.sourceId,
            value: source.payload.value,
          });
        }

        targets.forEach(({ targetId, value }) => {
          if (!targetId || !value?.currentTime || !draft.byId[targetId]) {
            return;
          }
          if (draft.byId[targetId].selectedTime === value.currentTime) {
            return;
          }
          draft.byId[targetId].selectedTime = value.currentTime;
        });
      },
    );
  },
});

export const soundingsActions = slice.actions;
export const soundingsReducer = slice.reducer;
